/* eslint-disable no-unused-vars */
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios from 'axios';
import Cookies from 'universal-cookie';
import i18n from '../i18n';

const baseURL = 'https://wasel.cloud/';
const cookies = new Cookies();

const axiosInstance = axios.create({
  baseURL,
});

export const axiosBaseQuery =
  () =>
  async ({ url, method, data, params, withToken, isFormData, tokenType }, { signal, getState }) => {
    try {
      const headers = {};
      const state = getState();
      const authToken = state.auth?.token;
      const customerToken = state.auth?.customerToken;
      
      // Get current language from i18n or localStorage, default to 'ar'
      const currentLang = i18n?.language || localStorage.getItem('i18nextLng') || 'ar';
      
      // Set Accept-Language header with both languages, current language first
      if (currentLang === 'ar') {
        headers['Accept-Language'] = 'ar,en';
      } else {
        headers['Accept-Language'] = 'en,ar';
      }

      // Strategy to find merchant_id for customer requests:
      // 1. Explicit merchant_id in params or data
      // 2. State's currentMerchantId (contextual)
      let requestMerchantId = params?.merchant_id || data?.merchant_id;
      
      // Handle FormData
      if (!requestMerchantId && data instanceof FormData) {
        requestMerchantId = data.get('merchant_id');
      }
      
      const contextualMerchantId = state.auth?.currentMerchantId;
      const resolvedMerchantId = requestMerchantId || contextualMerchantId;

      if (withToken) {
        let token = null;
        if (tokenType === 'customer') {
          // Try request ID first, then contextual ID
          token = (resolvedMerchantId ? state.auth?.customerSessions?.[resolvedMerchantId]?.token : null) || 
                  (contextualMerchantId ? state.auth?.customerSessions?.[contextualMerchantId]?.token : null);
        } else if (tokenType === 'admin') {
          token = authToken;
        } else if (!tokenType && !resolvedMerchantId) {
          // If no type and no merchant context, fallback to admin (legacy behavior)
          token = authToken;
        }
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      // Check if access-token is already provided anywhere
      const hasAccessToken = url.includes('access-token=') || params?.['access-token'];

      if (!hasAccessToken) {
        let tokenToUse = null;
        
        if (tokenType === 'admin') {
          tokenToUse = authToken;
        } else if (tokenType === 'customer') {
          // Try request ID first, then contextual ID
          tokenToUse = (resolvedMerchantId ? state.auth?.customerSessions?.[resolvedMerchantId]?.token : null) || 
                       (contextualMerchantId ? state.auth?.customerSessions?.[contextualMerchantId]?.token : null);
        } else if (!tokenType && resolvedMerchantId) {
           // Default fallback for website calls
           tokenToUse = (state.auth?.customerSessions?.[resolvedMerchantId]?.token) || 
                        (contextualMerchantId ? state.auth?.customerSessions?.[contextualMerchantId]?.token : null);
        } else if (!tokenType && !resolvedMerchantId && !url.includes('api/web')) {
           // Fallback for dashboard calls if no tokenType specified
           tokenToUse = authToken;
        }

        if (tokenToUse) {
          const separator = url.includes('?') ? '&' : '?';
          url = `${url}${separator}access-token=${tokenToUse}`;
        }
      }

      if (!isFormData) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await axiosInstance({
        url,
        method,
        data,
        params,
        headers,
        signal,
      });

      return { data: response.data };
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
        return {
          error: {
            status: 'CANCELLED',
            data: 'Request was cancelled',
          },
        };
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };